import { useCallback, useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Typography,
  Stack,
  Box,
  CircularProgress,
  Link,
  styled,
} from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { ROUTES } from '~/shared/constants';
import { PrimaryButton, OnboardingLayout } from '../components';
import { handleLogout as handleLogoutUtil } from '~/shared/utils/auth';
import {
  GET_ONBOARDING_STATUS,
  GET_MDI_ONBOARDING_URL,
} from '~/graphql/queries';
import useToast from '~/shared/hooks/useToast';
import MdiOnboardingModal from '~/shared/components/MdiOnboardingModal';
import Toast from '~/shared/components/Toast';
import { RiskTier } from '~/graphql/gen/graphql';
import { DNA } from '~/assets/icons';
import { useAcceptedInvitationContext } from '~/modules/invitation/hooks/useAcceptedInvitationContext';

interface OnboardingStatus {
  userExists: boolean;
  consentAcknowledged: boolean;
  completed: boolean;
  riskTier: RiskTier | null;
  hasDeclinedInvitation: boolean;
}

export const RiskTierResultsScreen = () => {
  const navigate = useNavigate();
  const { logout } = useAuth0();

  const { data, loading, error } = useQuery(GET_ONBOARDING_STATUS);
  const status: OnboardingStatus = {
    userExists: data?.onboardingStatus?.userExists ?? false,
    consentAcknowledged: data?.onboardingStatus?.consentAcknowledged ?? false,
    completed: data?.onboardingStatus?.completed ?? false,
    riskTier: data?.onboardingStatus?.riskTier ?? null,
    hasDeclinedInvitation:
      data?.onboardingStatus?.hasDeclinedInvitation ?? false,
  };

  // Check if user accepted an invitation and is now an EDITOR
  const { isEditor } = useAcceptedInvitationContext();

  const [mdiModalOpen, setMdiModalOpen] = useState(false);
  const [mdiUrl, setMdiUrl] = useState<string | null>(null);
  const { toast, showToast, hideToast } = useToast();
  const [getMdiOnboardingUrl] = useLazyQuery(GET_MDI_ONBOARDING_URL);

  const handleConnectWithDoctor = useCallback(async () => {
    setMdiModalOpen(true);
    setMdiUrl(null);

    try {
      const { data, error } = await getMdiOnboardingUrl();

      if (error) {
        showToast(
          'Failed to get MDI onboarding URL. Please try again.',
          'error',
        );
        setMdiModalOpen(false);
        return;
      }

      if (data?.getMdiOnboardingUrl) {
        setMdiUrl(data.getMdiOnboardingUrl);
      } else {
        setMdiModalOpen(false);
      }
    } catch (err) {
      showToast('Failed to request MDI onboarding. Please try again.', 'error');
      setMdiModalOpen(false);
    }
  }, [getMdiOnboardingUrl, showToast]);

  const handleBuildFamilyTree = () => {
    navigate(ROUTES.ONBOARDING.ROOT);
  };

  const handleMdiModalClose = useCallback(() => {
    setMdiModalOpen(false);
    setMdiUrl(null);

    // If user declined invitation, keep them on results screen
    // Otherwise, navigate to onboarding form
    if (status.hasDeclinedInvitation) {
      navigate(ROUTES.ONBOARDING.RESULTS);
    } else {
      navigate(ROUTES.ONBOARDING.ROOT);
    }
  }, [navigate, status.hasDeclinedInvitation]);

  const handleLogout = useCallback(() => {
    handleLogoutUtil(logout);
  }, [logout]);

  if (loading) {
    return (
      <OnboardingLayout
        showProgress={true}
        progressPercentage={100}
        showLogOut={true}
        onLogOut={handleLogout}
      >
        <Stack justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </Stack>
      </OnboardingLayout>
    );
  }

  if (error) {
    return (
      <OnboardingLayout
        showProgress={true}
        progressPercentage={100}
        showLogOut={true}
        onLogOut={handleLogout}
      >
        <Stack spacing={3} alignItems="center">
          <Typography variant="h5" color="error" textAlign="center">
            Unable to Load Results
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            We encountered an error while loading your risk assessment results.
            Please try refreshing the page or contact support if the problem
            persists.
          </Typography>
          <PrimaryButton
            variant="green"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </PrimaryButton>
        </Stack>
      </OnboardingLayout>
    );
  }

  const getTierContent = (
    tier: RiskTier | null,
    isEditor: boolean,
    hasDeclined: boolean,
  ) => {
    switch (tier) {
      case RiskTier.Tier_1: {
        return {
          headline:
            'Your answers suggest your family may be at higher risk for early-onset OTC deficiency.',
          supportingText1:
            'We can connect you with a licensed physician for no-cost testing & counseling. ',
          supportingText2:
            "You will be directed to our secure medical partner's portal to complete a short physician form. Some questions may be repeated because doctors must record information under medical privacy laws.",
          primaryButtonText: 'Connect with a Doctor',
          primaryButtonColor: 'brown' as const,
          primaryOnClick: handleConnectWithDoctor,
          secondaryLinkText: hasDeclined
            ? null
            : 'Skip & Build Your Family Tree',
          secondaryOnClick: hasDeclined ? null : handleBuildFamilyTree,
        };
      }
      case RiskTier.Tier_2: {
        let primaryButtonText = null;
        if (!hasDeclined) {
          primaryButtonText = isEditor
            ? 'Join Your Family Tree'
            : 'Build Your Family Tree';
        }

        return {
          headline:
            'You or your relatives may be carriers or have mild or late-onset forms of OTC deficiency.',
          supportingText1: `At this time, Quoiden's free testing is only focused on newborns and pregnancies.`,
          supportingText2:
            'You can start with education and a private family tree to understand inheritance patterns.',
          primaryButtonText,
          primaryButtonColor: 'green' as const,
          primaryOnClick: hasDeclined ? null : handleBuildFamilyTree,
          secondaryLinkText: null,
          secondaryOnClick: null,
        };
      }
      case RiskTier.Tier_3: {
        let primaryButtonText = null;
        if (!hasDeclined) {
          primaryButtonText = isEditor
            ? 'Join Your Family Tree'
            : 'Build Your Family Tree';
        }

        return {
          headline: 'Learn how OTC deficiency is inherited',
          supportingText1:
            'We recommend starting with general education and family-tree building',
          supportingText2: null,
          primaryButtonText,
          primaryButtonColor: 'green' as const,
          primaryOnClick: hasDeclined ? null : handleBuildFamilyTree,
          secondaryLinkText: null,
          secondaryOnClick: null,
        };
      }
      default: {
        return null;
      }
    }
  };

  const tierContent = getTierContent(
    status.riskTier,
    isEditor,
    status.hasDeclinedInvitation,
  );

  if (!tierContent) {
    return (
      <OnboardingLayout
        showProgress={true}
        progressPercentage={100}
        showLogOut={true}
        onLogOut={handleLogout}
      >
        <Typography color="error">
          Unable to load results. Please refresh the page and try again.
        </Typography>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      showProgress={true}
      progressPercentage={100}
      showLogOut={true}
      onLogOut={handleLogout}
    >
      <Stack direction="row" spacing={9} alignItems="center" mb={8}>
        <IconContainer>
          <DNA />
        </IconContainer>

        <HeadlineText>{tierContent.headline}</HeadlineText>
      </Stack>

      <Stack spacing={2} alignItems="center" mb={8}>
        <SupportingText1>{tierContent.supportingText1}</SupportingText1>

        <SupportingText2>{tierContent.supportingText2}</SupportingText2>
      </Stack>

      <Stack spacing={7}>
        {tierContent.primaryButtonText && tierContent.primaryOnClick && (
          <PrimaryButton
            variant={tierContent.primaryButtonColor}
            onClick={tierContent.primaryOnClick}
          >
            {tierContent.primaryButtonText}
          </PrimaryButton>
        )}

        {tierContent.secondaryLinkText && tierContent.secondaryOnClick && (
          <SecondaryLink onClick={tierContent.secondaryOnClick}>
            {tierContent.secondaryLinkText}
          </SecondaryLink>
        )}
      </Stack>

      <MdiOnboardingModal
        open={mdiModalOpen}
        onClose={handleMdiModalClose}
        url={mdiUrl}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={hideToast}
      />
    </OnboardingLayout>
  );
};

const IconContainer = styled(Box)({});

const SecondaryLink = styled(Link)({
  color: theme.palette.custom.darkBrown,
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 500,
  textAlign: 'center',
});

const HeadlineText = styled(Typography)({
  fontSize: '30px',
  fontWeight: 500,
  color: theme.palette.custom.textTitle,
});

const SupportingText1 = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: theme.palette.custom.textSupporting,
  textAlign: 'center',
  lineHeight: '25px',
});

const SupportingText2 = styled(Typography)({
  fontSize: '18px',
  fontWeight: 500,
  color: theme.palette.custom.textSupporting,
  textAlign: 'center',
  lineHeight: '25px',
  paddingTop: '18px',
  maxWidth: '600px',
});
